/**
 * Centralized field labels enum
 * Maintains consistent labeling across the entire application
 * (Tables, Forms, Filters, Reports, etc.)
 */

export enum FieldLabels {
  // Patient Information
  PATIENT_ID = 'Patient ID',
  PATIENT_NAME = 'Patient Name',
  PATIENT_AGE = 'Patient Age',
  PATIENT_SEX = 'Patient Sex',
  PATIENT_GENDER = 'Patient Gender',
  PATIENT_DOB = 'Patient Birth Date',
  MRN = 'MRN',

  // Patient Information (Short Forms - for tables)
  PATIENT_ID_SHORT = 'Pat. ID',
  PATIENT_AGE_SHORT = 'Pat. Age',
  PATIENT_SEX_SHORT = 'Pat. Sex',

  PATIENT_REPORT_AGE = 'Age',
  PATIENT_REPORT_SEX = 'Sex',

  // Study Information
  MODALITY = 'Modality',
  MODALITY_SHORT = 'Mod.',
  BODY_PART = 'Body Part',
  STUDY_DESCRIPTION = 'Study Description',
  STUDY_DESCRIPTION_SHORT = 'Study Desc.',
  ITEM_DESCRIPTION = 'Item Desc.',
  STUDY_DATE = 'Study Date',
  SCAN_DATE = 'Scan Date',
  RECEIVED_DATE = 'Received Date',
  SITE = 'Site',
  HISTORY = 'History',
  ITEM_CODE = 'Item Code',
  SERIES_COUNT = 'Ser/images ',

  // Order Information
  ORDER_NUMBER = 'Order No',
  VISIT_ID = 'Visit ID',
  ACCESSION_NUMBER = 'Accession Number',
  ACCESSION_NUMBER_SHORT = 'Acc. No',
  ACCESSION_NUMBER_SHORTEST = 'Acc No',
  REFERRING_DOCTOR = 'Ref Doc',
  REFERRING_PHYSICIAN = 'Referring Physician',
  REFERRING_PHYSICIAN_SHORT = 'Ref. Phys.',
  REGISTRATION_TYPE = 'Reg. Type',
  ORDER_DATETIME = 'Scheduled / Order Date',

  // Status & Assignment
  STATUS = 'Status',
  PRIORITY = 'Priority',
  ASSIGNED_TO = 'Assigned To',
  ASSIGNED_RADIOLOGIST = 'Assigned to',
  TECHNICIAN = 'Technician',
  REPORTED_BY = 'Reported by',
  SIGNED_BY = 'Signed by',

  // Technical Details
  AE_TITLE = 'AE Title',
  INSTANCES = 'Instances',
  SERIES = 'Series',
  DESCRIPTION = 'Description',

  // Actions
  ACTIONS = 'Actions',
}

/**
 * Permissions enum
 * Maintains consistent permission keys across the application
 */
export enum Permissions {
  CONFIRM_CASES = 'CONFIRM_CASES',
  CREATE_TECH_NOTES = 'CREATE_TECH_NOTES',
  ASSIGN_CASES = 'ASSIGN_CASES',
  MODIFY_STUDY_DETAILS = 'MODIFY_STUDY_DETAILS',
  UPLOAD_DICOM = 'UPLOAD_DICOM',
  // EXPORT_IMAGE is labelled "Ability to Download Study" in the UI; code kept for stability.
  EXPORT_IMAGE = 'EXPORT_IMAGE',
  REPORT_FROM_PACS = 'REPORT_FROM_PACS',
  VIEW_DISPATCH_REPORT = 'VIEW_DISPATCH_REPORT',
  QC_WORKSTATION = 'QC_WORKSTATION',
  MARK_NOT_FOR_REPORTING = 'MARK_NOT_FOR_REPORTING',
  // EDIT_SIGNED_REPORTS is labelled "Can re-report signed-off study" in the UI; code kept for stability.
  EDIT_SIGNED_REPORTS = 'EDIT_SIGNED_REPORTS',
  // MANAGE_LIB_FOLDERS is labelled "Can Push Cases to Academic Server" in the UI; code kept for stability.
  MANAGE_LIB_FOLDERS = 'MANAGE_LIB_FOLDERS',
}

/**
 * Display labels for the PACS workflow status (order_workflow.ow_pacs_status).
 *
 * Decouples what tables render from the stored DB enum: the value stays
 * NOT_FOR_REPORTING everywhere (filters, status toggles, backend), but the
 * status column shows a shorter label so it fits the constrained, sticky
 * status column instead of overflowing. Anything not listed here falls back to
 * the raw value with underscores swapped for spaces.
 */
export const PACS_STATUS_LABELS: Record<string, string> = {
  NOT_FOR_REPORTING: 'NOT READY',
};

export const getPacsStatusLabel = (status?: string | null): string => {
  if (!status) return '';
  return PACS_STATUS_LABELS[status] ?? status.replaceAll('_', ' ');
};
